/**
 * Custom hook for accessing audit history data
 * 
 * In demo mode: Uses localStorage via auditService
 * In live mode: Fetches from backend API via approvalsApi (StageExecution table)
 */

import { useMemo, useState, useEffect } from 'react';
import { auditService } from '@/services/auditService';
import { approvalsApi } from '@/services/approvalsApi';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { AuditEntry, AuditSummary, AuditActionType } from '@/types/audit';
import type { TimesheetWorkflowHistory, ApprovalResponseHistory } from '@/types/workflow-status';

/**
 * Maps a workflow history response to audit entries
 */
function mapWorkflowHistoryToAuditEntries(history: TimesheetWorkflowHistory): AuditEntry[] {
  const entries: AuditEntry[] = [];

  // Add workflow started entry if we have startedAt
  if (history.startedAt) {
    entries.push({
      id: `workflow-started-${history.workflowInstanceId}`,
      timesheetId: history.timesheetId,
      timestamp: history.startedAt,
      actionType: 'submitted',
      actorId: 'system',
      actorName: 'System',
      actorRole: 'system',
      comment: history.workflowName ? `Workflow "${history.workflowName}" started` : 'Workflow started',
    });
  }

  // Map each stage's approval responses to audit entries
  for (const stage of history.stages) {
    for (const response of stage.responses) {
      const actionType: AuditActionType = response.decision === 'approved' ? 'approved' : 'rejected';
      
      let actorName = response.approverName || 'Unknown';
      let actorRole = 'approver';
      
      // Handle delegated approvals
      if (response.isDelegated && response.delegatedFromName) {
        actorRole = `delegate for ${response.delegatedFromName}`;
      }

      entries.push({
        id: response.id,
        timesheetId: history.timesheetId,
        timestamp: response.respondedAt,
        actionType,
        actorId: response.approverId,
        actorName,
        actorRole,
        comment: response.comments || undefined,
        previousStatus: 'submitted',
        newStatus: response.decision === 'approved' ? 'approved' : 'rejected',
      });
    }
  }

  return entries;
}

/**
 * Creates an audit summary from workflow history
 */
function mapWorkflowHistoryToAuditSummary(history: TimesheetWorkflowHistory): AuditSummary {
  const summary: AuditSummary = {
    totalChanges: 0,
  };

  // Set submitted time from workflow start
  if (history.startedAt) {
    summary.submittedAt = history.startedAt;
    summary.totalChanges++;
  }

  // Find approval/rejection from stage responses
  for (const stage of history.stages) {
    for (const response of stage.responses) {
      summary.totalChanges++;
      
      if (response.decision === 'approved') {
        // Use the latest approval
        if (!summary.approvedAt || response.respondedAt > summary.approvedAt) {
          summary.approvedAt = response.respondedAt;
          summary.approvedBy = response.approverName || 'Unknown';
        }
      } else if (response.decision === 'rejected') {
        // Use the latest rejection
        if (!summary.rejectedAt || response.respondedAt > summary.rejectedAt) {
          summary.rejectedAt = response.respondedAt;
          summary.rejectedBy = response.approverName || 'Unknown';
        }
      }
    }
  }

  return summary;
}

export function useAuditHistory(timesheetId: number) {
  const { getScreenMode } = useDataSource();
  // Use per-screen mode for timesheet screen instead of global mode
  const isLive = getScreenMode('timesheet') === 'live';
  const [liveEntries, setLiveEntries] = useState<AuditEntry[]>([]);
  const [liveSummary, setLiveSummary] = useState<AuditSummary>({ totalChanges: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch from API in live mode
  useEffect(() => {
    // Skip workflow history for draft timesheets with temporary IDs
    if (!isLive || !timesheetId || timesheetId <= 0) {
      setLiveEntries([]);
      setLiveSummary({ totalChanges: 0 });
      return;
    }

    let cancelled = false;

    async function fetchWorkflowHistory() {
      setIsLoading(true);
      setError(null);
      
      try {
        const history = await approvalsApi.getWorkflowHistory(timesheetId);
        
        if (cancelled) return;

        const entries = mapWorkflowHistoryToAuditEntries(history);
        const summary = mapWorkflowHistoryToAuditSummary(history);
        
        setLiveEntries(entries);
        setLiveSummary(summary);
      } catch (err) {
        if (cancelled) return;
        
        console.error('Failed to fetch workflow history:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch history');
        setLiveEntries([]);
        setLiveSummary({ totalChanges: 0 });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchWorkflowHistory();

    return () => {
      cancelled = true;
    };
  }, [isLive, timesheetId]);

  // Demo mode: use localStorage via auditService
  const demoEntries = useMemo(() => {
    if (isLive) return [];
    return auditService.getAuditTrail(timesheetId);
  }, [isLive, timesheetId]);

  const demoSummary = useMemo(() => {
    if (isLive) return { totalChanges: 0 };
    return auditService.getAuditSummary(timesheetId);
  }, [isLive, timesheetId]);

  // Select entries based on mode
  const auditEntries = isLive ? liveEntries : demoEntries;
  const auditSummary = isLive ? liveSummary : demoSummary;

  const sortedEntries = useMemo(() => {
    return [...auditEntries].sort((a, b) => 
      b.timestamp.getTime() - a.timestamp.getTime()
    );
  }, [auditEntries]);

  return {
    auditEntries: sortedEntries,
    auditSummary,
    hasHistory: auditEntries.length > 0,
    isLoading,
    error,
  };
}
